import { Accordion, AccordionItem, Selection } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React from "react";
import { useTranslation } from "react-i18next";

const faqKeys = ["q1", "q2", "q3", "q4", "q5", "q6"] as const;

const FAQSection: React.FC = () => {
  const { t } = useTranslation();
  const [selectedKeys, setSelectedKeys] = React.useState<Selection>(new Set(["1"]));

  const faqs = faqKeys.map((key, index) => ({
    id: String(index + 1),
    question: t(`modern.workBrain.faq.items.${key}.question`),
    answer: t(`modern.workBrain.faq.items.${key}.answer`),
  }));

  return (
    <motion.section
      className="landing-snap-section flex min-h-screen items-center bg-white py-16 md:py-20"
    >
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-[30px] md:text-[44px] font-bold text-gray-900 mb-4">
            {t("modern.workBrain.faq.title", "Questions before you start")}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t("modern.workBrain.faq.subtitle", "The practical details behind memory, sources, agent workflows, approvals, platforms, and privacy.")}
          </p>
        </motion.div>

        <motion.div
          className="max-w-4xl mx-auto bg-gray-50 rounded-3xl p-6 md:p-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Accordion
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            variant="light"
            itemClasses={{
              base: "py-0 w-full",
              title: "font-normal text-medium",
              trigger: "px-2 py-0 data-[hover=true]:bg-default-100 rounded-lg h-14 flex items-center",
              indicator: "text-medium",
              content: "text-small px-2",
            }}
            showDivider={false}
          >
            {faqs.map((faq) => {
              const isOpen = Array.from(selectedKeys).includes(faq.id);
              return (
                <AccordionItem
                  key={faq.id}
                  aria-label={faq.question}
                  textValue={faq.question}
                  hideIndicator
                  title={
                    <div className="flex items-center gap-4 w-full">
                      <div className={`transition-transform duration-300 ${isOpen ? "rotate-45" : "rotate-0"}`}>
                        <Icon icon="lucide:plus" className="text-gray-500 text-xl" />
                      </div>
                      <span className={`text-base md:text-lg font-semibold text-gray-900 ${isOpen ? "text-gray-900" : "text-gray-700"}`}>
                        {faq.question}
                      </span>
                    </div>
                  }
                >
                  <div className="pl-9 pb-4 text-gray-600 leading-relaxed text-base">
                    {faq.answer}
                  </div>
                </AccordionItem>
              );
            })}
          </Accordion>
        </motion.div>

        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-gray-600 mb-4">
            {t("modern.workBrain.faq.stillHaveQuestions", "Still have questions?")}
          </p>
          <a
            href="mailto:support@youwo.ai"
            className="inline-flex items-center gap-2 text-cornflowerblue font-semibold hover:underline"
          >
            {t("modern.workBrain.faq.contactSupport", "We're here to help. Reach out at support@youwo.ai.")}
          </a>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default FAQSection;
